import { CURRENCY_SYMBOLS } from 'common/DataTypeFormatter';
import { ColumnFormat } from 'common/types/viewColumn';
import isUndefined from 'lodash/isUndefined';
import times from 'lodash/times';
import has from 'lodash/has';
import some from 'lodash/some';
import keys from 'lodash/keys';
import toString from 'lodash/toString';

// Based on the maximum precision value a user can set in Overview Page
export const DEMICAL_PLACES_LIMIT = 10;
const requiredFormatKeys = ['precisionStyle', 'currencyStyle', 'groupSeparator', 'precision'];

/**
 * Generate integer format to export in XLSX file keeping it's datatype as a number / custom
 * @param format format object from column formatting
 * @param viewFormat format object from overview page
 */
export const generateIntegerFormat = (format: ColumnFormat | undefined, viewFormat: ColumnFormat) => {
  const hasSetColumnFormat = some(keys(format), (key) => requiredFormatKeys.includes(key));
  const referenceFormat = isUndefined(format) || !hasSetColumnFormat ? viewFormat : format;
  const { precisionStyle, currencyStyle, groupSeparator } = referenceFormat;
  let thousandsSeparator = '';
  if (has(referenceFormat, 'groupSeparator')) {
    thousandsSeparator = groupSeparator ?? '';
  } else {
    // noCommas came from commaify
    const commaifySeparator = toString(viewFormat.noCommas) === 'true' ? '' : ',';
    thousandsSeparator = viewFormat.groupSeparator ?? commaifySeparator;
  }
  const defaultFormat = `#${thousandsSeparator}##0`;
  if (precisionStyle === 'currency') {
    const currencySymbol = currencyStyle ?? viewFormat?.currencyStyle ?? 'USD';
    // It is important to add quotation marks around the currency symbol because Excel
    // might render it as an invalid currency if not quoted.
    // e.g., for Philippine Peso: '"Php"#,##0'
    const currencyFormat = `"${CURRENCY_SYMBOLS[currencySymbol as keyof typeof CURRENCY_SYMBOLS] ?? ''}"`;
    return `${currencyFormat}${defaultFormat}`;
  } else if (precisionStyle === 'percentage') {
    return `${defaultFormat}%`;
  }
  return defaultFormat;
};

/**
 * Generates a number format string with decimal places for exporting to an XLSX file,
 * ensuring the cell value is treated as a number in Excel.
 * Based on the base format string generated from `generateIntegerFormat`.
 * Currently limited to 10 decimal places.
 * @param currentFormat format string generated from generateIntegerFormat
 * @param colKey field name
 */
export const generateDecimalFormat = (currentFormat: string, colKey: string) => {
  const decimalPlaces = times(DEMICAL_PLACES_LIMIT, (i) => {
    const decimals = '0'.repeat(i + 1);
    const isPercentage = currentFormat.includes('%');
    let format = currentFormat;
    if (isPercentage) {
      format = format.split('%')[0];
    }
    // This will generate 10 classes for each column processed by generateExcelStyles().
    // Currently, we need to output a number type in Excel.
    // Using processCellCallback() alone can result in Excel treating the value as text if a number is returned as a formatted string.
    // This behavior has caused formatting issues and has been raised as a bug.
    return {
      id: `decimal-${i + 1}-formatted-cell-${colKey}`,
      dataType: 'Number',
      numberFormat: {
        format: `${format}.${decimals}${isPercentage ? '%' : ''}`
      }
    };
  });
  return decimalPlaces;
};
